// 哨兵管理系统 - 后端接口层（sentinel-manage-api）
// 统一返回结构：{ code, data, msg }，成功 code=200，业务数据在 res.data
// 与小程序 Mini-Manage/api/index.js 一一对应，仅响应解包方式不同：
//   web 端拦截器已解包为 {code,data,msg}，故页面用 res.data 取业务数据；
//   小程序 request.js 解包为 data 本体，页面用 res 取业务数据。
import axios from './base'

/* ============================================================
 * MOCK 开关：无需后端即可本地预览完整界面。
 * 接真实后端时，把 USE_MOCK 改为 false 即可（路由/baseURL 已在 base.js 配置）。
 * ============================================================ */
const USE_MOCK = false

/* 无后端接口、仅本地预览的深度模块（这些端点后端尚未实现，继续走 mock） */
const MOCK_ALWAYS = new Set([
  'monitor', 'dataCenter', 'asset', 'track', 'behavior',
  'audit', 'message', 'report', 'workorder'
])
function useMock(slug) {
  return USE_MOCK || MOCK_ALWAYS.has(slug)
}

function mockOk(data) {
  // 模拟一点网络延迟，更接近真实交互
  return new Promise(resolve => setTimeout(() => resolve({ code: 200, data, msg: '' }), 120))
}

/* ============================ mock 数据 ============================ */
const mockFactories = [
  { id: 1, name: '杭州余杭智能仓', longitude: '120.0123', latitude: '30.2819', created_at: '2026-05-12 09:24:11' },
  { id: 2, name: '上海青浦配送中心', longitude: '121.1234', latitude: '31.1567', created_at: '2026-06-03 14:02:38' },
  { id: 3, name: '苏州工业园厂区', longitude: '120.5853', latitude: '31.2989', created_at: '2026-07-21 10:45:02' }
]

const mockDeviceBind = {
  stat3: [
    { value: 18, label: '绑定设备' },
    { value: 2, label: '近7日新增' },
    { value: 1, label: '待处理告警' }
  ],
  binds: [
    { id: 1, name: '叉车A01', sn: 'FL-A01-8821', online: true, time: '2026-08-01 08:12', remark: '主仓作业' },
    { id: 2, name: '叉车A02', sn: 'FL-A02-7735', online: false, time: '2026-08-02 09:40', remark: '' },
    { id: 3, name: '叉车B01', sn: 'FL-B01-6610', online: true, time: '2026-08-03 11:05', remark: '配送中心' },
    { id: 4, name: '巡检车C01', sn: 'CV-C01-5592', online: true, time: '2026-08-05 13:22', remark: '园区巡检' }
  ],
  history: [
    { id: 1, name: '叉车A01', sn: 'FL-A01-8821', badge: '已解绑', info: '设备回收更换', op: '2026-07-20 16:30 操作员' },
    { id: 2, name: '叉车Z09', sn: 'FL-Z09-2201', badge: '已换绑', info: '由原车辆换绑至新车辆', op: '2026-07-28 10:11 管理员' }
  ]
}

const mockDeviceHistory = [
  { id: 1, name: '叉车A01', sn: 'FL-A01-8821', badge: '已解绑', info: '设备回收更换', op: '2026-07-20 16:30 操作员' },
  { id: 2, name: '叉车Z09', sn: 'FL-Z09-2201', badge: '已换绑', info: '由原车辆换绑至新车辆', op: '2026-07-28 10:11 管理员' },
  { id: 3, name: '叉车A02', sn: 'FL-A02-7735', badge: '已换绑', info: '更换电池模块后重新绑定', op: '2026-08-10 14:05 管理员' }
]

const mockSecurity = {
  zstats: { zoneCount: 2, todayOverspeed: 7 },
  rules: [
    {
      id: 1, name: '主仓装卸限速区', speed: 15, time_range: '08:00-18:00', factory_id: 1,
      polygon: [
        { lat: 30.2819, lng: 120.0123 }, { lat: 30.2840, lng: 120.0140 },
        { lat: 30.2830, lng: 120.0180 }, { lat: 30.2800, lng: 120.0150 }
      ]
    },
    {
      id: 2, name: '园区主干道限速', speed: 25, time_range: '', factory_id: 3,
      polygon: [
        { lat: 31.2989, lng: 120.5853 }, { lat: 31.3010, lng: 120.5880 }, { lat: 31.3000, lng: 120.5920 }
      ]
    }
  ]
}

const mockAlerts = {
  stats: [
    { level: 0, value: 1 },
    { level: 1, value: 3 },
    { level: 2, value: 5 }
  ],
  alerts: [
    { id: 1, level: 0, done: false, title: '叉车A01 急减速', sub: '叉车A01 / FL-A01-8821', detail: '车速从 22km/h 骤降至 3km/h，疑似危险操作', time: '2026-08-18 09:12' },
    { id: 2, level: 1, done: false, title: '叉车B01 超速', sub: '叉车B01 / FL-B01-6610', detail: '在限速 15km/h 区域测得 28km/h', time: '2026-08-18 10:40' },
    { id: 3, level: 1, done: true, title: '巡检车C01 偏离路线', sub: '巡检车C01 / CV-C01-5592', detail: '离开规划巡检路线 120m', time: '2026-08-18 11:05' },
    { id: 4, level: 2, done: false, title: '设备低电量提醒', sub: '叉车A02 / FL-A02-7735', detail: '电量低于 15%', time: '2026-08-18 13:20' },
    { id: 5, level: 2, done: false, title: '夜间作业提醒', sub: '叉车A01 / FL-A01-8821', detail: '非工作时间启动', time: '2026-08-18 02:15' }
  ]
}

const mockMonitor = {
  total: 4,
  online: 3,
  markers: [
    { id: 1, lat: 30.2819, lng: 120.0123, title: '叉车A01', sn: 'FL-A01-8821', status: 'online', speed: 2.1 },
    { id: 3, lat: 31.1567, lng: 121.1234, title: '叉车B01', sn: 'FL-B01-6610', status: 'online', speed: 0.8 },
    { id: 4, lat: 31.2989, lng: 120.5853, title: '巡检车C01', sn: 'CV-C01-5592', status: 'online', speed: 3.4 },
    { id: 2, lat: 30.2900, lng: 120.0200, title: '叉车A02', sn: 'FL-A02-7735', status: 'offline', speed: null }
  ]
}

const mockDataCenter = {
  eff: { cats: ['周一', '周二', '周三', '周四', '周五', '周六', '周日'], series: [120, 132, 101, 134, 90, 230, 210] },
  report: { cats: ['1月', '2月', '3月', '4月', '5月', '6月'], series: [320, 432, 401, 534, 590, 630] },
  heat: { cats: ['A区', 'B区', 'C区', 'D区', 'E区'], series: [80, 120, 60, 200, 95] },
  track: { cats: ['00', '04', '08', '12', '16', '20', '24'], series: [10, 22, 180, 240, 160, 40, 12] }
}

const mockSubAccounts = [
  { id: 1, account: 'operator01', mobile: '13800001111', role: '现场操作员', status: 1 },
  { id: 2, account: 'manager01', mobile: '13800002222', role: '厂区管理员', status: 1 },
  { id: 3, account: 'viewer01', mobile: '13800003333', role: '只读观察员', status: 0 }
]

const mockEnterprise = {
  verified: 0,
  name: '杭州智行物联科技有限公司',
  credit_code: '91330100MA2XXXXXX',
  legal_person: '张明',
  license_no: '3301XXXXXXXXXX'
}

/* ============ 厂区管理 ============ */
export function getFactories() {
  if (USE_MOCK) return mockOk({ list: mockFactories })
  return axios.request({ url: '/user/factories', method: 'get' })
}
export function createFactory(data) {
  if (USE_MOCK) return mockOk({ id: Date.now() })
  return axios.request({ url: '/user/factories', method: 'post', data })
}
export function updateFactory(id, data) {
  if (USE_MOCK) return mockOk({ id })
  return axios.request({ url: `/user/factories/${id}`, method: 'put', data })
}
export function deleteFactory(id) {
  if (USE_MOCK) return mockOk({ id })
  return axios.request({ url: `/user/factories/${id}`, method: 'delete' })
}
export function searchLocation(keyword) {
  if (USE_MOCK) return mockOk({
    list: [
      { title: keyword || '查询结果', address: '浙江省杭州市余杭区智格路', lat: '30.2819', lng: '120.0123' },
      { title: (keyword || '地点') + '（示例点B）', address: '浙江省杭州市西湖区文一西路', lat: '30.2592', lng: '120.1300' }
    ]
  })
  return axios.request({ url: '/user/factories/search', method: 'get', params: { keyword } })
}

/* ============ 设备绑定管理 ============ */
export function getDeviceBind() {
  if (USE_MOCK) return mockOk(mockDeviceBind)
  return axios.request({ url: '/user/devices/binds', method: 'get' })
}
export function getDeviceHistory() {
  if (USE_MOCK) return mockOk(mockDeviceHistory)
  return axios.request({ url: '/user/devices/history', method: 'get' })
}
export function lookupDevice(code) {
  if (USE_MOCK) return mockOk({ id: 1, sn: code, code, type: 1 })
  return axios.request({ url: '/user/devices/lookup', method: 'get', params: { code } })
}
export function bindDevice(code) {
  if (USE_MOCK) return mockOk({ id: Date.now() })
  return axios.request({ url: '/user/devices/bind', method: 'post', data: { code } })
}
export function unbindDevice(id) {
  if (USE_MOCK) return mockOk({ id })
  return axios.request({ url: `/user/devices/${id}/unbind`, method: 'put' })
}
export function rebindDevice(id, data) {
  if (USE_MOCK) return mockOk({ id })
  return axios.request({ url: `/user/devices/${id}/rebind`, method: 'put', data })
}
export function updateDevice(id, data) {
  if (USE_MOCK) return mockOk({ id })
  return axios.request({ url: `/user/devices/${id}`, method: 'put', data })
}

/* ============ 安全管理（限速区域） ============ */
export function getSecurity() {
  if (USE_MOCK) return mockOk(mockSecurity)
  return axios.request({ url: '/user/security', method: 'get' })
}
export function createSpeedZone(data) {
  if (USE_MOCK) return mockOk({ id: Date.now() })
  return axios.request({ url: '/user/security/zones', method: 'post', data })
}
export function updateSpeedZone(id, data) {
  if (USE_MOCK) return mockOk({ id })
  return axios.request({ url: `/user/security/zones/${id}`, method: 'put', data })
}
export function deleteSpeedZone(id) {
  if (USE_MOCK) return mockOk({ id })
  return axios.request({ url: `/user/security/zones/${id}`, method: 'delete' })
}

/* ============ 告警中心 ============ */
export function getAlerts() {
  if (USE_MOCK) return mockOk(mockAlerts)
  return axios.request({ url: '/user/alerts', method: 'get' })
}
export function handleAlert(id, note) {
  if (USE_MOCK) return mockOk({ id })
  return axios.request({ url: `/user/alerts/${id}/handle`, method: 'put', data: { note } })
}

/* ============ 工作台 / 实时监控 / 数据中心 ============ */
export function getMonitor() {
  if (useMock('monitor')) return mockOk(mockMonitor)
  return axios.request({ url: '/user/monitor', method: 'get' })
}
export function getDataCenter() {
  if (useMock('dataCenter')) return mockOk(mockDataCenter)
  return axios.request({ url: '/user/data-center', method: 'get' })
}
export function getMine() {
  if (USE_MOCK) return mockOk({})
  return axios.request({ url: '/user/mine', method: 'get' })
}

/* ============ 账号管理（子账号 / 企业） ============ */
export function getSubAccounts() {
  if (USE_MOCK) return mockOk({ list: mockSubAccounts })
  return axios.request({ url: '/user/sub-accounts', method: 'get' })
}
export function createSubAccount(data) {
  if (USE_MOCK) return mockOk({ id: Date.now() })
  return axios.request({ url: '/user/sub-accounts', method: 'post', data })
}
export function updateSubAccount(id, data) {
  if (USE_MOCK) return mockOk({ id })
  return axios.request({ url: `/user/sub-accounts/${id}`, method: 'put', data })
}
export function deleteSubAccount(id) {
  if (USE_MOCK) return mockOk({ id })
  return axios.request({ url: `/user/sub-accounts/${id}`, method: 'delete' })
}
export function getEnterprise() {
  if (USE_MOCK) return mockOk(mockEnterprise)
  return axios.request({ url: '/user/enterprise', method: 'get' })
}
export function submitEnterprise(data) {
  if (USE_MOCK) return mockOk({})
  return axios.request({ url: '/user/enterprise', method: 'post', data })
}

/* ============ 设备资产管理（全量台账，web 端深度模块） ============ */
const ASSET_TYPES = { 1: '叉车', 2: '特殊车辆', 3: '车辆' }
const ASSET_STATUS = { 1: '在线', 2: '离线', 3: '故障', 4: '未激活' }
const mockAssets = [
  { id: 1, name: '叉车A01', sn: 'FL-A01-8821', type: 1, factory_id: 1, factory_name: '杭州余杭智能仓', status: 1, battery: 82, speed: 2.1, operator: '王强', sim: '13800001111', firmware: 'v2.3.1', last_online: '2026-08-18 16:02', mileage: 12840 },
  { id: 2, name: '叉车A02', sn: 'FL-A02-7735', type: 1, factory_id: 1, factory_name: '杭州余杭智能仓', status: 2, battery: 15, speed: 0, operator: '李伟', sim: '13800002222', firmware: 'v2.3.1', last_online: '2026-08-18 09:40', mileage: 9620 },
  { id: 3, name: '叉车B01', sn: 'FL-B01-6610', type: 1, factory_id: 2, factory_name: '上海青浦配送中心', status: 1, battery: 64, speed: 0.8, operator: '赵敏', sim: '13800003333', firmware: 'v2.4.0', last_online: '2026-08-18 16:00', mileage: 15230 },
  { id: 4, name: '巡检车C01', sn: 'CV-C01-5592', type: 2, factory_id: 3, factory_name: '苏州工业园厂区', status: 1, battery: 91, speed: 3.4, operator: '陈刚', sim: '13800004444', firmware: 'v2.4.0', last_online: '2026-08-18 15:58', mileage: 4310 },
  { id: 5, name: '叉车B02', sn: 'FL-B02-4418', type: 1, factory_id: 2, factory_name: '上海青浦配送中心', status: 3, battery: 43, speed: 0, operator: '孙磊', sim: '13800005555', firmware: 'v2.3.0', last_online: '2026-08-17 22:11', mileage: 20110 },
  { id: 6, name: '叉车A03', sn: 'FL-A03-3390', type: 1, factory_id: 1, factory_name: '杭州余杭智能仓', status: 4, battery: 0, speed: 0, operator: '—', sim: '13800006666', firmware: 'v2.4.0', last_online: '—', mileage: 0 },
  { id: 7, name: '叉车C02', sn: 'FL-C02-2271', type: 1, factory_id: 3, factory_name: '苏州工业园厂区', status: 1, battery: 77, speed: 1.5, operator: '周婷', sim: '13800007777', firmware: 'v2.4.0', last_online: '2026-08-18 15:55', mileage: 7780 },
  { id: 8, name: '叉车B03', sn: 'FL-B03-1188', type: 1, factory_id: 2, factory_name: '上海青浦配送中心', status: 2, battery: 28, speed: 0, operator: '吴昊', sim: '13800008888', firmware: 'v2.3.1', last_online: '2026-08-18 11:20', mileage: 13440 },
  { id: 9, name: '牵引车D01', sn: 'TR-D01-9012', type: 3, factory_id: 3, factory_name: '苏州工业园厂区', status: 1, battery: 88, speed: 4.2, operator: '郑凯', sim: '13800009999', firmware: 'v2.4.0', last_online: '2026-08-18 15:50', mileage: 28900 },
  { id: 10, name: '叉车A04', sn: 'FL-A04-7755', type: 1, factory_id: 1, factory_name: '杭州余杭智能仓', status: 1, battery: 56, speed: 1.1, operator: '冯雪', sim: '13800010101', firmware: 'v2.4.0', last_online: '2026-08-18 15:48', mileage: 6150 },
  { id: 11, name: '叉车C03', sn: 'FL-C03-6644', type: 1, factory_id: 3, factory_name: '苏州工业园厂区', status: 3, battery: 35, speed: 0, operator: '蒋琳', sim: '13800020202', firmware: 'v2.3.0', last_online: '2026-08-17 19:30', mileage: 18720 },
  { id: 12, name: '巡检车C02', sn: 'CV-C02-5533', type: 2, factory_id: 3, factory_name: '苏州工业园厂区', status: 2, battery: 19, speed: 0, operator: '韩冰', sim: '13800030303', firmware: 'v2.4.0', last_online: '2026-08-18 08:05', mileage: 5020 }
]
function assetStat() {
  const s = { total: mockAssets.length, online: 0, offline: 0, fault: 0, inactive: 0 }
  mockAssets.forEach(a => { if (a.status === 1) s.online++; else if (a.status === 2) s.offline++; else if (a.status === 3) s.fault++; else s.inactive++ })
  return s
}
export function getAssets(params = {}) {
  if (useMock('asset')) {
    let list = mockAssets.slice()
    if (params.factory_id) list = list.filter(a => a.factory_id === +params.factory_id)
    if (params.type) list = list.filter(a => a.type === +params.type)
    if (params.status) list = list.filter(a => a.status === +params.status)
    if (params.keyword) {
      const k = String(params.keyword).toLowerCase()
      list = list.filter(a => a.name.toLowerCase().includes(k) || a.sn.toLowerCase().includes(k) || a.operator.toLowerCase().includes(k))
    }
    return mockOk({ list, total: list.length, stat: assetStat() })
  }
  return axios.request({ url: '/assets', method: 'get', params })
}
export function getAssetDetail(id) {
  if (useMock('asset')) return mockOk(mockAssets.find(a => a.id === id) || {})
  return axios.request({ url: `/assets/${id}`, method: 'get' })
}
export function updateAsset(id, data) {
  if (useMock('asset')) return mockOk({ id })
  return axios.request({ url: `/assets/${id}`, method: 'put', data })
}
export function batchAssetAction(ids, action) {
  if (useMock('asset')) return mockOk({ ids, action })
  return axios.request({ url: '/assets/batch', method: 'put', data: { ids, action } })
}

/* ============ 轨迹回放 ============ */
const mockTrackDevices = mockAssets.filter(a => a.status !== 4).map(a => ({ id: a.id, name: a.name, sn: a.sn }))
function genTrack(deviceId, date) {
  const dev = mockAssets.find(a => a.id === deviceId) || mockAssets[0]
  const baseLng = 120.0123 + (deviceId % 3) * 0.05
  const baseLat = 30.2819 + (deviceId % 4) * 0.02
  const points = []
  let lng = baseLng, lat = baseLat
  for (let h = 8; h <= 18; h++) {
    for (let m = 0; m < 60; m += 15) {
      lng += (Math.sin((h + m) * 0.7) * 0.0012)
      lat += (Math.cos((h + m) * 0.5) * 0.0009)
      const speed = Math.round((1 + Math.abs(Math.sin((h + m) * 0.9)) * 30) * 10) / 10
      points.push({
        lng: +lng.toFixed(6), lat: +lat.toFixed(6),
        speed, ts: `${date} ${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`,
        over: speed > 25
      })
    }
  }
  return {
    device: { id: dev.id, name: dev.name, sn: dev.sn },
    center: { lng: baseLng, lat: baseLat },
    points,
    overSpeedCount: points.filter(p => p.over).length
  }
}
export function getTrackDevices() {
  if (useMock('track')) return mockOk({ list: mockTrackDevices })
  return axios.request({ url: '/track/devices', method: 'get' })
}
export function getTrack(deviceId, date) {
  if (useMock('track')) return mockOk(genTrack(deviceId, date || '2026-08-18'))
  return axios.request({ url: '/track', method: 'get', params: { device_id: deviceId, date } })
}

/* ============ 驾驶行为 & 安全分析 ============ */
const mockBehaviorEvents = [
  { id: 1, device: '叉车B01', sn: 'FL-B01-6610', type: '超速', level: 1, value: '28km/h', zone: '主仓装卸限速区', time: '2026-08-18 10:40', location: '杭州余杭智能仓' },
  { id: 2, device: '叉车A01', sn: 'FL-A01-8821', type: '急减速', level: 0, value: '22→3km/h', zone: '—', time: '2026-08-18 09:12', location: '杭州余杭智能仓' },
  { id: 3, device: '巡检车C01', sn: 'CV-C01-5592', type: '偏离路线', level: 1, value: '120m', zone: '—', time: '2026-08-18 11:05', location: '苏州工业园厂区' },
  { id: 4, device: '叉车B03', sn: 'FL-B03-1188', type: '疲劳驾驶', level: 2, value: '连续4h', zone: '—', time: '2026-08-18 14:22', location: '上海青浦配送中心' },
  { id: 5, device: '叉车A02', sn: 'FL-A02-7735', type: '超速', level: 1, value: '31km/h', zone: '园区主干道限速', time: '2026-08-17 16:48', location: '苏州工业园厂区' },
  { id: 6, device: '叉车C03', sn: 'FL-C03-6644', type: '急刹车', level: 0, value: '25→0km/h', zone: '—', time: '2026-08-17 15:10', location: '苏州工业园厂区' },
  { id: 7, device: '叉车B02', sn: 'FL-B02-4418', type: '超速', level: 2, value: '35km/h', zone: '园区主干道限速', time: '2026-08-17 13:33', location: '上海青浦配送中心' },
  { id: 8, device: '叉车A04', sn: 'FL-A04-7755', type: '急减速', level: 0, value: '18→2km/h', zone: '—', time: '2026-08-18 08:51', location: '杭州余杭智能仓' }
]
const mockSafetyDrivers = [
  { name: '王强', score: 94, overspeed: 2, harsh: 1, fatigue: 0 },
  { name: '赵敏', score: 90, overspeed: 4, harsh: 2, fatigue: 0 },
  { name: '陈刚', score: 88, overspeed: 1, harsh: 3, fatigue: 0 },
  { name: '周婷', score: 85, overspeed: 5, harsh: 1, fatigue: 1 },
  { name: '孙磊', score: 72, overspeed: 9, harsh: 4, fatigue: 2 },
  { name: '郑凯', score: 69, overspeed: 11, harsh: 3, fatigue: 1 },
  { name: '蒋琳', score: 61, overspeed: 14, harsh: 5, fatigue: 3 }
]
const mockSafetyFactories = [
  { name: '杭州余杭智能仓', score: 90, overspeed: 6, fault: 0 },
  { name: '苏州工业园厂区', score: 82, overspeed: 20, fault: 2 },
  { name: '上海青浦配送中心', score: 74, overspeed: 16, fault: 1 }
]
export function getBehaviorEvents(params = {}) {
  if (useMock('behavior')) {
    let list = mockBehaviorEvents.slice()
    if (params.type) list = list.filter(e => e.type === params.type)
    if (params.factory_id) list = list.filter(e => e.location === (mockFactories.find(f => f.id === +params.factory_id) || {}).name)
    return mockOk({ list, total: list.length })
  }
  return axios.request({ url: '/behavior/events', method: 'get', params })
}
export function getSafetyRanking() {
  if (useMock('behavior')) return mockOk({ drivers: mockSafetyDrivers, factories: mockSafetyFactories })
  return axios.request({ url: '/behavior/safety', method: 'get' })
}

/* ============ 操作审计日志 ============ */
const mockAuditLogs = [
  { id: 1, operator: 'admin', module: '厂区管理', action: '编辑', target: '杭州余杭智能仓', ip: '192.168.1.12', time: '2026-08-18 15:30' },
  { id: 2, operator: 'manager01', module: '设备管理', action: '绑定', target: '叉车B01 / FL-B01-6610', ip: '192.168.1.34', time: '2026-08-18 14:50' },
  { id: 3, operator: 'admin', module: '安全管理', action: '新增', target: '主仓装卸限速区', ip: '192.168.1.12', time: '2026-08-18 11:20' },
  { id: 4, operator: 'operator01', module: '账号管理', action: '登录', target: 'operator01', ip: '192.168.1.56', time: '2026-08-18 09:05' },
  { id: 5, operator: 'admin', module: '账号管理', action: '编辑', target: '子账号：厂区管理员', ip: '192.168.1.12', time: '2026-08-17 18:40' },
  { id: 6, operator: 'manager01', module: '设备管理', action: '解绑', target: '叉车A02 / FL-A02-7735', ip: '192.168.1.34', time: '2026-08-17 16:10' },
  { id: 7, operator: 'admin', module: '安全管理', action: '删除', target: '旧装卸限速区', ip: '192.168.1.12', time: '2026-08-17 10:25' },
  { id: 8, operator: 'viewer01', module: '数据中心', action: '导出', target: '7月运营报表', ip: '192.168.1.78', time: '2026-08-16 17:00' }
]
export function getAuditLogs(params = {}) {
  if (useMock('audit')) {
    let list = mockAuditLogs.slice()
    if (params.module) list = list.filter(l => l.module === params.module)
    if (params.operator) list = list.filter(l => l.operator.includes(params.operator))
    return mockOk({ list, total: list.length })
  }
  return axios.request({ url: '/audit/logs', method: 'get', params })
}

/* ============ 消息 & 推送中心 ============ */
const mockMessages = [
  { id: 1, title: '叉车B01 超速告警', content: '在限速 15km/h 区域测得 28km/h', type: '告警', read: 0, time: '2026-08-18 10:40' },
  { id: 2, title: '叉车A02 低电量提醒', content: '电量低于 15%，请及时充电', type: '系统', read: 0, time: '2026-08-18 13:20' },
  { id: 3, title: '系统升级完成', content: '设备固件已推送至 v2.4.0', type: '系统', read: 0, time: '2026-08-18 08:00' },
  { id: 4, title: '叉车B02 故障上报', content: '设备上报故障码 E203，请安排检修', type: '工单', read: 1, time: '2026-08-17 22:11' },
  { id: 5, title: '巡检车C01 偏离路线', content: '离开规划巡检路线 120m', type: '告警', read: 1, time: '2026-08-18 11:05' },
  { id: 6, title: '新增厂区成功', content: '苏州工业园厂区已创建', type: '系统', read: 1, time: '2026-08-17 09:00' }
]
export function getMessages(params = {}) {
  if (useMock('message')) {
    let list = mockMessages.slice()
    if (params.type) list = list.filter(m => m.type === params.type)
    const unread = mockMessages.filter(m => m.read === 0).length
    return mockOk({ list, total: list.length, unread })
  }
  return axios.request({ url: '/messages', method: 'get', params })
}
export function readMessage(id) {
  if (useMock('message')) return mockOk({ id })
  return axios.request({ url: `/messages/${id}/read`, method: 'put' })
}
export function readAllMessages() {
  if (useMock('message')) return mockOk({})
  return axios.request({ url: '/messages/read-all', method: 'put' })
}

/* ============ 报表中心 ============ */
const mockReports = {
  onlineTrend: { cats: ['周一', '周二', '周三', '周四', '周五', '周六', '周日'], series: [96, 94, 98, 92, 97, 90, 95] },
  faultRate: { cats: ['3月', '4月', '5月', '6月', '7月', '8月'], series: [2.1, 1.8, 2.4, 1.5, 1.2, 0.9] },
  overspeedTop: [
    { name: '叉车C03', count: 14 }, { name: '郑凯(牵引车D01)', count: 11 }, { name: '孙磊(叉车B02)', count: 9 },
    { name: '周婷(叉车C02)', count: 5 }, { name: '赵敏(叉车B01)', count: 4 }, { name: '王强(叉车A01)', count: 2 }
  ],
  workTrend: { cats: ['3月', '4月', '5月', '6月', '7月', '8月'], series: [320, 432, 401, 534, 590, 630] }
}
export function getReports(params = {}) {
  if (useMock('report')) return mockOk(mockReports)
  return axios.request({ url: '/reports', method: 'get', params })
}

/* ============ 运维工单 ============ */
const mockCommandDevices = mockAssets.filter(a => a.status !== 4).map(a => ({ id: a.id, name: a.name, sn: a.sn, status: a.status }))
const mockWorkOrders = [
  { id: 'WO-20260818-001', device: '叉车B02', sn: 'FL-B02-4418', type: '故障', priority: 4, status: 0, assignee: '—', creator: 'system', desc: '设备上报故障码 E203，无法启动', created_at: '2026-08-17 22:11', finished_at: null },
  { id: 'WO-20260818-002', device: '叉车A02', sn: 'FL-A02-7735', type: '保养', priority: 2, status: 1, assignee: '李伟', creator: 'manager01', desc: '电池模块更换后例行保养', created_at: '2026-08-16 10:00', finished_at: null },
  { id: 'WO-20260818-003', device: '巡检车C01', sn: 'CV-C01-5592', type: '巡检', priority: 3, status: 1, assignee: '陈刚', creator: 'manager01', desc: '月度安全巡检', created_at: '2026-08-15 09:30', finished_at: null },
  { id: 'WO-20260818-004', device: '叉车C03', sn: 'FL-C03-6644', type: '故障', priority: 3, status: 2, assignee: '蒋琳', creator: 'system', desc: '急刹车传感器异常', created_at: '2026-08-14 14:20', finished_at: '2026-08-17 16:00' },
  { id: 'WO-20260818-005', device: '叉车A01', sn: 'FL-A01-8821', type: '保养', priority: 2, status: 2, assignee: '王强', creator: 'manager01', desc: '季度保养', created_at: '2026-08-10 11:00', finished_at: '2026-08-12 15:30' },
  { id: 'WO-20260818-006', device: '叉车B03', sn: 'FL-B03-1188', type: '故障', priority: 4, status: 1, assignee: '孙磊', creator: 'system', desc: '转向系统异响', created_at: '2026-08-18 08:30', finished_at: null },
  { id: 'WO-20260818-007', device: '叉车A04', sn: 'FL-A04-7755', type: '巡检', priority: 1, status: 0, assignee: '—', creator: 'manager01', desc: '新设备入库巡检', created_at: '2026-08-18 07:50', finished_at: null }
]
export function getWorkOrders(params = {}) {
  if (useMock('workorder')) {
    let list = mockWorkOrders.slice()
    if (params.status !== undefined && params.status !== '') list = list.filter(w => w.status === +params.status)
    if (params.type) list = list.filter(w => w.type === params.type)
    if (params.keyword) {
      const k = String(params.keyword).toLowerCase()
      list = list.filter(w => w.id.toLowerCase().includes(k) || w.device.toLowerCase().includes(k) || w.desc.toLowerCase().includes(k))
    }
    const stat = { pending: 0, processing: 0, done: 0 }
    mockWorkOrders.forEach(w => { if (w.status === 0) stat.pending++; else if (w.status === 1) stat.processing++; else stat.done++ })
    return mockOk({ list, total: list.length, stat })
  }
  return axios.request({ url: '/work-orders', method: 'get', params })
}
export function createWorkOrder(data) {
  if (useMock('workorder')) return mockOk({ id: 'WO-' + Date.now() })
  return axios.request({ url: '/work-orders', method: 'post', data })
}
export function dispatchWorkOrder(id, data) {
  if (useMock('workorder')) return mockOk({ id })
  return axios.request({ url: `/work-orders/${id}/dispatch`, method: 'put', data })
}
export function processWorkOrder(id, data) {
  if (useMock('workorder')) return mockOk({ id })
  return axios.request({ url: `/work-orders/${id}/process`, method: 'put', data })
}
export function closeWorkOrder(id, data) {
  if (useMock('workorder')) return mockOk({ id })
  return axios.request({ url: `/work-orders/${id}/close`, method: 'put', data })
}
export function getWorkOrderDevices() {
  if (useMock('workorder')) return mockOk({ list: mockCommandDevices })
  return axios.request({ url: '/work-orders/devices', method: 'get' })
}
